import React, {ReactNode, useMemo} from 'react';
import {createTheme, Theme, ThemeOptions, ThemeProvider} from '@mui/material/styles';
import {TypographyStyleOptions} from "@mui/material/styles/createTypography";
import {FormThemeSettings} from "crqlar-react-common/dist/models/forms/form";

interface FormThemeProviderProps {
    theme?: FormThemeSettings;
    children: ReactNode;
}

/**
 * Validates if a string is a valid hex color format
 * Supports both RGB (#RRGGBB) and RGBA (#RRGGBBAA) formats
 */
const isValidHexaColor = (color: string | undefined): boolean => {
    if (!color) return false;
    return /^#[0-9a-fA-F]{6}$/.test(color) || /^#[0-9a-fA-F]{8}$/.test(color);
};

/**
 * FormThemeProvider wraps form components with a custom MUI theme
 * based on FormThemeSettings from the form configuration.
 *
 * Maps the simplified backend API structure to full MUI theme structure.
 */
const FormThemeProvider: React.FC<FormThemeProviderProps> = ({theme, children}) => {
    // Create a custom MUI theme by mapping backend structure to MUI structure
    const customTheme: Theme = useMemo(() => {
        if (!theme) {
            return createTheme();
        }

        // Map simplified backend structure to MUI ThemeOptions
        const themeOptions: ThemeOptions = {};

        console.log("Debuggin", theme.palette, isValidHexaColor(theme.palette?.primary))

        // Map palette
        if (theme.palette) {
            const palette = theme.palette;
            themeOptions.palette = {
                mode: 'light',
                primary: palette.primary && isValidHexaColor(palette.primary) ? {
                    main: palette.primary,
                    contrastText: palette.contrastText || '#ffffff',
                } : {
                    main: '#061c88'
                },
                secondary: palette.secondary && isValidHexaColor(palette.secondary) ? {
                    main: palette.secondary,
                    contrastText: palette.contrastText || '#ffffff',
                } : {
                    main: '#989898'
                },
                background: {
                    default: palette.background,
                    paper: palette.background,
                },
                text: {
                    primary: palette.textPrimary || '#000000',
                    secondary: palette.textSecondary || '#757575',
                    disabled: palette.textDisabled || '#9e9e9e',
                },
            };
        }

        // Map typography
        if (theme.typography || theme.palette) {
            const typography = theme.typography;
            const palette = theme.palette;

            themeOptions.typography = {
                fontFamily: typography?.fontFamily ? `${typography.fontFamily}, "Helvetica", "Arial", sans-serif` : typography?.fontFamily,
                fontSize: typography?.fontSize,
            };

            // Map heading styles to all heading variants
            const headingStyle: TypographyStyleOptions = {
                fontSize: typography?.heading?.fontSize,
                fontWeight: typography?.heading?.fontWeight,
                lineHeight: typography?.heading?.lineHeight,
                letterSpacing: typography?.heading?.letterSpacing,
                color: palette?.textPrimary
            };

            const questionStyle: TypographyStyleOptions = {
                fontSize: typography?.question?.fontSize || '16px',
                fontWeight: typography?.question?.fontWeight || 'normal',
                lineHeight: typography?.question?.lineHeight || '1.5',
                letterSpacing: typography?.question?.letterSpacing || 'normal',
                color: palette?.textPrimary
            };

            const bodyStyle: TypographyStyleOptions = {
                fontSize: typography?.body?.fontSize,
                fontWeight: typography?.body?.fontWeight,
                lineHeight: typography?.body?.lineHeight,
                letterSpacing: typography?.body?.letterSpacing,
                color: palette?.textSecondary
            };

            const buttonStyle: TypographyStyleOptions = {
                fontSize: typography?.button?.fontSize,
                fontWeight: typography?.button?.fontWeight,
                color: palette?.textPrimary
                // lineHeight: typography.button.lineHeight,
                // letterSpacing: typography.button.letterSpacing,
            }

            themeOptions.typography = {
                ...themeOptions.typography,
                h3: headingStyle,
                h6: questionStyle,
                body1: bodyStyle,
                body2: bodyStyle,
                button: buttonStyle
            };
        }

        // Map shape
        if (theme.shape) {
            themeOptions.shape = {
                borderRadius: theme.shape.borderRadius,
            };
        }

        return createTheme(themeOptions);
    }, [theme]);

    return (
        <ThemeProvider theme={customTheme}>
            {children}
        </ThemeProvider>
    );
};

export default FormThemeProvider;
